import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Form, Select, Input, Button, Space, Tag, Alert, message, Divider, Switch,
} from 'antd';
import { ApiOutlined, LinkOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { fmtDateTime } from '../../lib/format';
import {
  getTicketingConfig, saveTicketingConfig, testTicketingConfig,
  TicketingConfig, TicketingProvider,
} from '../../services/ticketingService';

const { Title, Paragraph, Text, Link } = Typography;

const PROVIDERS: { value: TicketingProvider; label: string }[] = [
  { value: 'none', label: 'None (record internally only)' },
  { value: 'jira', label: 'Jira Cloud' },
  { value: 'mock', label: 'Mock (testing)' },
];

const TicketingIntegrationPage: React.FC = () => {
  const [config, setConfig] = useState<TicketingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [provider, setProvider] = useState<TicketingProvider>('none');
  const [enabled, setEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [projectKey, setProjectKey] = useState('');
  const [email, setEmail] = useState('');
  const [issueType, setIssueType] = useState('Bug');
  const [apiToken, setApiToken] = useState('');

  const apply = (c: TicketingConfig) => {
    setConfig(c);
    setProvider(c.provider || 'none');
    setEnabled(!!c.enabled);
    setBaseUrl(c.jira_base_url || '');
    setProjectKey(c.jira_project_key || '');
    setEmail(c.jira_email || '');
    setIssueType(c.jira_issue_type || 'Bug');
    setApiToken('');
  };

  const load = async () => {
    setLoading(true);
    try {
      apply(await getTicketingConfig());
    } catch {
      message.error('Could not load the ticketing configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isJira = provider === 'jira';

  const save = async () => {
    setSaving(true);
    try {
      const c = await saveTicketingConfig({
        provider,
        enabled,
        jira_base_url: baseUrl.trim(),
        jira_project_key: projectKey.trim(),
        jira_email: email.trim(),
        jira_issue_type: issueType.trim() || 'Bug',
        api_token: apiToken.trim() || undefined, // omit to keep the stored token
      });
      apply(c);
      message.success('Ticketing settings saved.');
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.response?.data?.message || 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await testTicketingConfig();
      if (r.ok) message.success(`Connection OK${r.provider ? ` (${r.provider})` : ''}.`);
      else message.error(`Connection failed: ${r.error || 'unknown error'}`);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not run the connection test. Save your settings first.');
    } finally {
      setTesting(false);
    }
  };

  const tokenSet = !!config?.token_set;

  return (
    <div style={{ maxWidth: 720 }}>
      <Title level={3}><ApiOutlined /> Ticketing integration</Title>
      <Paragraph type="secondary">
        Connect the engineering ticketing system so support cases Dana escalates become tickets your
        engineering team triages. This is a platform-wide integration; the API token is stored encrypted
        and never shown again after saving.
      </Paragraph>

      <Card loading={loading}>
        {config && (
          <Alert
            style={{ marginBottom: 16 }}
            type={config.enabled && config.provider !== 'none' ? 'success' : 'info'}
            showIcon
            message={
              config.provider === 'none'
                ? 'No provider configured — escalated cases are recorded internally only.'
                : (
                  <span>
                    Provider <Tag>{config.provider}</Tag>
                    {config.enabled ? <Tag color="green">enabled</Tag> : <Tag>disabled</Tag>}
                    {tokenSet && <Tag color="blue">token set</Tag>}
                    {config.updated_at ? ` · updated ${fmtDateTime(config.updated_at)}` : ''}
                  </span>
                )
            }
          />
        )}

        <Form layout="vertical">
          <Form.Item label="Provider">
            <Select value={provider} onChange={(v) => setProvider(v)} options={PROVIDERS} />
          </Form.Item>

          <Form.Item
            label="Enabled"
            help="When off, cases are recorded but not sent to the provider."
          >
            <Switch checked={enabled} onChange={setEnabled} />
          </Form.Item>

          {isJira && (
            <>
              <Divider orientation="left" plain>Jira Cloud</Divider>

              <Form.Item label="Base URL" help="Your Jira site, e.g. https://kazipeople.atlassian.net">
                <Input
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://kazipeople.atlassian.net"
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item label="Project key" help="The project issues are created in, e.g. SCRUM (the key, not the name).">
                <Input value={projectKey} onChange={(e) => setProjectKey(e.target.value)} placeholder="SCRUM" autoComplete="off" />
              </Form.Item>

              <Form.Item label="Account email" help="The Atlassian account the API token belongs to.">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@kazipeople.com" autoComplete="off" />
              </Form.Item>

              <Form.Item label="Issue type" help="Issue type for created tickets (must exist in the project).">
                <Input value={issueType} onChange={(e) => setIssueType(e.target.value)} placeholder="Bug" autoComplete="off" />
              </Form.Item>

              <Form.Item
                label="API token"
                help={tokenSet ? 'Leave blank to keep the stored token. Enter a new token to replace it.' : 'Create one at id.atlassian.com → Security → API tokens.'}
              >
                <Input.Password
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  placeholder={tokenSet ? '•••••••••• (unchanged)' : 'Paste your Jira API token'}
                  autoComplete="off"
                />
              </Form.Item>
            </>
          )}

          <Space>
            <Button type="primary" loading={saving} onClick={save}>Save</Button>
            <Button icon={<LinkOutlined />} loading={testing} onClick={test} disabled={provider === 'none'}>
              Test connection
            </Button>
          </Space>
        </Form>

        <Divider />
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          <SafetyCertificateOutlined />{' '}
          <Text strong>How it works:</Text> case content is redacted (secrets, emails, IDs) and
          classification-gated before it leaves the platform — restricted cases are never sent. Save your
          settings, then use <Text strong>Test connection</Text> to verify credentials before enabling.
          Create a Jira API token at{' '}
          <Link href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank">id.atlassian.com</Link>.
        </Paragraph>
      </Card>
    </div>
  );
};

export default TicketingIntegrationPage;
